import React, { type ReactElement, memo, useMemo } from 'react';
import { isFamilySubsInTariff, tariffBuyCustomFeatures, welcomeDefaultTariff } from 'reactApp/appHelpers/featuresHelpers';
import { enabledExpensivePlanTrial } from 'reactApp/appHelpers/featuresHelpers/features/forceTrial';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import type { Product } from 'reactApp/types/Billing';

import styles from './FastCheckoutSidebar.css';
import type { FastCheckoutSidebarQueryParams } from './FastCheckoutSidebar.types';

interface FeaturesListProps {
    customList?: string[];
    product: Product;
}

export const FeaturesList = memo(({ customList, product }: FeaturesListProps): ReactElement => {
    const {
        cancelSending: cancelSendingFromQuery,
        freeSpace: freeSpaceFromQuery,
        features: featuresFromQuery,
    }: FastCheckoutSidebarQueryParams = getQueryParams();

    const cancelSending = cancelSendingFromQuery === 'true';
    const freeSpace = freeSpaceFromQuery === 'true';

    const queryFeatures = useMemo(() => (featuresFromQuery && tariffBuyCustomFeatures[featuresFromQuery]) || [], [featuresFromQuery]);

    const list = useMemo(() => {
        if (customList?.length) {
            return customList;
        }

        // номер списка для семейного тарифа в конфиге фич
        const familyFeatures = tariffBuyCustomFeatures['8'];
        if (product.isFamily && familyFeatures?.length) {
            return familyFeatures;
        }
        if (queryFeatures?.length) {
            return queryFeatures;
        }
        const autodeleteText = 'Автоудаление папок';
        const cancelSendingText = 'Отмена отправки писем';
        const additionalFeatures = cancelSending ? [cancelSendingText] : [];

        const familyText = 'Добавление ещё 3 участников';
        const autodeleteTextNew = 'Автоудаление папок в выбранное время';

        const base = [
            'Без рекламы в\u00A0Почте\u00A0и\u00A0Облаке',
            'Загрузка файлов до 100 ГБ в\u00A0Облако',
            'Отправка файлов до 100 ГБ в\u00A0письмах',
        ];

        // tempexp_17086-start
        if (freeSpace || welcomeDefaultTariff?.tariff === product?.id) {
            return [...base, 'Делитесь местом с близкими', 'Свободная память в телефоне c автозагрузкой'];
        }
        // tempexp_17086-end

        if (isFamilySubsInTariff && !enabledExpensivePlanTrial) {
            return [...base, 'Делитесь местом с близкими', ...additionalFeatures, autodeleteText];
        }

        if (enabledExpensivePlanTrial && product.hasTrial) {
            return [...base, ...additionalFeatures, familyText, autodeleteTextNew];
        }

        return [...base, ...additionalFeatures, autodeleteText, 'Приоритетная поддержка'];
    }, [customList, product, cancelSending, freeSpace, queryFeatures]);

    return (
        <ol className={styles.features}>
            {list.map((text, index) => (
                <li key={index}>{text}</li>
            ))}
        </ol>
    );
});

FeaturesList.displayName = 'FeaturesList';
